import { useNavigate, useParams } from "react-router-dom";
import { Store as StoreType } from "../../__generated__/graphql";
import { useCart } from "../../core/cart";
import Partition from "../../components/common/partition";
import { useAuth } from "../../core/auth";
import { track } from "@amplitude/analytics-browser";
import LoginBottomSheet from "../../components/common/login-bottom-sheet";
import TimeSaleSection from "../../components/store/time-sale-section";
import TimeSaleHeader from "./components/time-sale-header";
import ReserveSection from "../../components/store/reserve-section";
import ReserveReviewGrid from "./components/reserve-review-grid";
import StoreDetails from "../../components/store/store-details";
import StoreHeader from "../../components/store/store-header";
import StoreBottomCta from "./components/store-bottom-cta";
import { StoreDeletedModal } from "./components/store-deleted-modal";
import AppDownloadBottomSheet from "../../components/app-download/app-download-bottom-sheet";
import StoreSkeleton from "./components/store-skeleton";
import StoreSeo from "./components/store-seo";
import StoreCouponSection from "./components/store-coupon-section";
import ReviewCard from "./review/components/review-card";
import ReviewDetailModal from "../../components/review/review-detail-modal";
import QuantitySelectBottomSheet from "./components/quantity-select-bottom-sheet";
import ReserveConfirmBottomSheet from "./components/reserve-confirm-bottom-sheet";
import { getCategoryName } from "./utils/store-category";
import {
  getExposeType,
  isPackSetted,
  isTimeSaling,
  isReserveTimeEnabled,
  isPackSoldOut,
} from "../../utils/store-expose";
import { getDayOfWeekIndex, getTomorrowDayOfWeekIndex } from "../../utils/reserve-time";
import { getTotalAmount, getTotalDiscount } from "./utils/store-cart";
import { useStoreScroll } from "./hooks/use-store-scroll";
import { useStoreTimers } from "./hooks/use-store-timers";
import { useStorePageData } from "./hooks/use-store-page-data";
import { useStorePageState } from "./hooks/use-store-page-state";
import { useStoreActions } from "./hooks/use-store-actions";

const Store = () => {
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const storeId = id ? Number(id) : undefined;

  const { reserveRef, timeSaleRef, reviewRef, scrollToSection } =
    useStoreScroll();
  const {
    store,
    packData,
    packReviewData,
    loading,
    packLoading,
    likeStore,
    cancelLike,
    isStoreDeleted,
    refetchStore,
  } = useStorePageData(storeId);
  const timeLeft = useStoreTimers(store?.closingHours);
  const state = useStorePageState();
  const {
    isSheetOpen,
    setIsSheetOpen,
    selectedReview,
    setSelectedReview,
    isTimeSaleTooltipOpen,
    setIsTimeSaleTooltipOpen,
    isQuantitySheetOpen,
    setIsQuantitySheetOpen,
    selectedProductForQuantity,
    setSelectedProductForQuantity,
    isReserveConfirmOpen,
    setIsReserveConfirmOpen,
    isTomorrowForConfirm,
    setIsTomorrowForConfirm,
    isLoginSheetOpen,
    setIsLoginSheetOpen,
  } = state;

  const { addToCart, cart, removeFromCart } = useCart();

  const totalAmount = getTotalAmount(cart);
  const actions = useStoreActions({
    store,
    cart,
    addToCart: addToCart as (product: unknown, quantity: number, storeInfo: unknown) => void,
    removeFromCart,
    isLoggedIn,
    navigate,
    likeStore,
    cancelLike,
    setIsLoginSheetOpen,
    setSelectedProductForQuantity,
    setIsQuantitySheetOpen,
    setIsReserveConfirmOpen,
    setIsTomorrowForConfirm,
    selectedProductForQuantity,
    setIsSheetOpen,
    totalAmount,
  });
  const {
    onClickProduct,
    onClickPurchage,
    onClickPackPurchase,
    executePackPurchase,
    onClickLike,
    onClickNoti,
    handleQuantityConfirm,
  } = actions;

  const dayOfWeek = getDayOfWeekIndex(new Date());
  const tomorrowDayOfWeek = getTomorrowDayOfWeekIndex();

  const isPackSettedFlag = isPackSetted(store);
  const isTimeSalingFlag = isTimeSaling(store);
  const isReserveTimeEnabledFlag = isReserveTimeEnabled(
    store?.reserveTime as Record<string, { enabled?: boolean }> | undefined,
    dayOfWeek,
    tomorrowDayOfWeek
  );
  const isPackSoldOutFlag = isPackSoldOut(store);

  const exposeType = getExposeType(store?.pack);
  const categoryName = getCategoryName(store?.pack?.category);
  const totalDiscount = getTotalDiscount(cart);

  if (loading || packLoading) {
    return <StoreSkeleton />;
  }

  // Real-time Purchase Section Component
  const timeSaleSection = (
    <div ref={timeSaleRef}>
      <div className="w-full bg-white">
        <TimeSaleHeader
          isTimeSaling={isTimeSalingFlag}
          timeLeft={timeLeft}
          isTooltipOpen={isTimeSaleTooltipOpen}
          onTooltipToggle={() => setIsTimeSaleTooltipOpen((prev) => !prev)}
        />
        <TimeSaleSection
          store={store as StoreType}
          timeLeft={timeLeft}
          onClickProduct={onClickProduct}
          onClickLike={onClickNoti}
          bestReviewId={store?.bestReview?.id}
          cart={cart}
        />
      </div>
      <Partition color="light" height="thick" />
    </div>
  );

  // Reservation Section Component
  const reserveSection = isPackSettedFlag ? (
    <div ref={reserveRef} className="bg-white">
      <div className="w-full px-5 pt-6 pb-2 flex items-center gap-1">
        <h2 className="text-xl font-bold text-black">
          {exposeType === "내일" ? "내일" : "오늘"}의 {categoryName} 랜덤박스
          예약하기
        </h2>
      </div>
      <ReserveSection
        isTimeSaling={isTimeSalingFlag}
        packData={packData}
        isReserveTimeEnabled={isReserveTimeEnabledFlag}
        isPackSoldOut={isPackSoldOutFlag}
        onClickPackPurchase={executePackPurchase}
        store={store}
        dayOfWeek={dayOfWeek}
        isTodayEnabled={store?.pack?.isTodayEnabled}
        isTomorrowEnabled={store?.pack?.isTomorrowEnabled}
        isOverTodayPickupTime={store?.pack?.isOverTodayPickupTime}
        isLiked={store?.isLiked}
        likeCount={store?.likes ? store?.likes?.length : 0}
        onClickNoti={(e) => {
          e.stopPropagation();
          onClickNoti(
            store?.id as number,
            store?.isLiked as boolean,
            store?.title as string,
          );
        }}
        reviewCount={store?.reviewCount || 0}
        packItems={store?.pack?.packItems}
        bestReviewId={store?.bestReview?.id}
        categoryName={categoryName}
        isLoggedIn={isLoggedIn}
        onLoginRequired={() => setIsLoginSheetOpen(true)}
      />

      <ReserveReviewGrid
        packReviews={
          packReviewData?.getReviewByStoreId?.filter(
            (r) => r.reviewImages?.length && r.reviewImages[0]?.img
          ) ?? []
        }
        storeId={store?.id}
        onReviewSelect={setSelectedReview}
      />

      <Partition color="light" height="thick" />
    </div>
  ) : null;

  return (
    <div className="container mx-auto bg-[#f6f6f6]">
      <StoreSeo store={store as StoreType | undefined} />
      {/* 매장 상세 페이지, 삭제되지 않은 매장만 노출 */}
      {store && (
        <>
          <StoreHeader
            loading={loading}
            store={store as StoreType}
            onClickLike={onClickLike}
          />

          <StoreDetails
            loading={loading}
            store={store as StoreType}
            onTabClick={scrollToSection}
          />

          {store?.availableStoreCoupon && (
            <>
              <Partition color="light" height="thin" />
              <StoreCouponSection
                store={store as StoreType}
                onWebCTA={() => setIsSheetOpen(true)}
                isLoggedIn={isLoggedIn}
                onLoginCTA={() => setIsLoginSheetOpen(true)}
                isLiked={store?.isLiked}
                onLikeStateChanged={refetchStore}
              />
            </>
          )}

          <Partition color="light" height="thick" />

          {/* Render sections based on isTimeSaling */}
          {isTimeSalingFlag ? (
            <>
              {timeSaleSection}
              {reserveSection}
            </>
          ) : (
            <>
              {reserveSection}
              {timeSaleSection}
            </>
          )}

          {/* Review Section */}
          <div ref={reviewRef} className="w-full pb-44 bg-white">
            <div className="px-5 pt-6 pb-4 flex justify-between items-center">
              <h2 className="text-xl font-bold text-black">
                리뷰 {store?.reviewCount}
              </h2>
              <div
                className="bg-gray-100 rounded-lg px-3 py-2 cursor-pointer hover:bg-gray-200 transition-colors"
                onClick={() => {
                  track("매장 페이지 리뷰 전체보기 클릭");
                  navigate(`/store/${store?.id}/review`);
                }}
              >
                <p className="text-gray-700 text-sm font-medium">전체보기</p>
              </div>
            </div>
            <ReviewCard
              storeId={store?.id as number}
              bestReviewId={store?.bestReview?.id}
              setSelectedReview={setSelectedReview}
            />
          </div>

          <StoreBottomCta
            cartLength={cart.length}
            totalAmount={totalAmount}
            totalDiscount={totalDiscount}
            store={store as StoreType}
            packData={packData}
            isTimeSaling={isTimeSalingFlag}
            isPackSetted={isPackSettedFlag}
            exposeType={exposeType}
            isPackSoldOut={isPackSoldOutFlag}
            onPurchase={onClickPurchage}
            onPackPurchase={onClickPackPurchase}
            onNoti={onClickNoti}
          />

          <LoginBottomSheet
            isOpen={isLoginSheetOpen}
            onClose={() => setIsLoginSheetOpen(false)}
            discountRate={packData?.pack?.discountRate || 0}
          />
        </>
      )}

      {/* 매장 삭제 모달 */}
      <StoreDeletedModal isOpen={isStoreDeleted} />
      <AppDownloadBottomSheet
        isOpen={isSheetOpen}
        onClose={() => setIsSheetOpen(false)}
      />

      <ReviewDetailModal
        review={selectedReview}
        onClose={() => setSelectedReview(null)}
      />

      <QuantitySelectBottomSheet
        isOpen={isQuantitySheetOpen}
        onClose={() => {
          setIsQuantitySheetOpen(false);
          setSelectedProductForQuantity(null);
        }}
        product={selectedProductForQuantity}
        onConfirm={handleQuantityConfirm}
      />

      <ReserveConfirmBottomSheet
        isOpen={isReserveConfirmOpen}
        onClose={() => setIsReserveConfirmOpen(false)}
        onConfirm={(isTomorrow) => {
          setIsReserveConfirmOpen(false);
          executePackPurchase(isTomorrow);
        }}
        exposeType={isTomorrowForConfirm ? "내일" : "오늘"}
        store={store}
      />
    </div>
  );
};

export default Store;
